import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { describePosUnits, posReserveUnits } from '../utils/posUtil';
import '../css/Pos.css';

/**
 * What to do with a product tapped on the Reserve Stocks tab.
 *
 * Two different jobs start from the same card, so the card asks rather than
 * guessing: carry stock out of the storeroom onto the shelf, or record what
 * arrived into the storeroom (and correct its details).
 *
 * @param {object} props
 * @param {object} props.product - the product whose card was tapped
 * @param {() => void} props.onReplenish - opens Replenish Active Stocks
 * @param {() => void} props.onEdit - opens Edit Reserve Stocks
 * @param {() => void} props.onClose
 * @returns {JSX.Element}
 */
function PosReserveActionModal({ product, onReplenish, onEdit, onClose }) {
  const inReserve = posReserveUnits(product);

  return createPortal(
    // No backdrop click-to-close (CLAUDE.md > Behavior): the × is the way out.
    <div className="pos-modal-overlay">
      <div className="pos-modal pos-modal-narrow" role="dialog" aria-modal="true" aria-label={`Choose an action for ${product?.name || 'product'}`}>
        <header className="pos-modal-head">
          <h3>{product?.name}</h3>
          <button type="button" className="pos-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        <div className="pos-modal-body">
          <p className="pos-action-lead">
            In reserve: <strong>{describePosUnits(inReserve, product)}</strong>
          </p>

          <div className="pos-action-list">
            <button
              type="button"
              className="pos-action-option"
              onClick={onReplenish}
              disabled={inReserve === 0}
            >
              <span className="pos-action-title">Replenish Active Stocks</span>
              <span className="pos-action-desc">
                {inReserve === 0
                  ? 'Nothing in reserve to move yet.'
                  : 'Move some of the reserve onto the shelf, where sales draw from.'}
              </span>
            </button>

            <button type="button" className="pos-action-option" onClick={onEdit}>
              <span className="pos-action-title">Edit Reserve Stocks</span>
              <span className="pos-action-desc">
                Add newly arrived supplies to the storeroom, or correct the reserve and its packing.
              </span>
            </button>
          </div>
        </div>

        <footer className="pos-modal-foot">
          <span className="pos-foot-spacer" />
          <button type="button" className="pos-btn pos-btn-ghost" onClick={onClose}>Cancel</button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

export default PosReserveActionModal;
